import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/hooks';
import { useUI } from '@/hooks/ui/useUI';
import { shiftService } from '@/api/domains/shift';
import { shiftRequestSchema } from '@/schemas/attendance/shiftRequest.schema';
import type { ShiftRequestFormData } from '@/api/domains/shift/shift.types';
import { formatDateToYYYYMMDD } from '@/utils';
import { injectValidationErrors } from '@/utils/formValidationErrors';


interface UseShiftRequestFormProps {
  onSuccess?: () => void;
}

export const useShiftRequestForm = ({ onSuccess }: UseShiftRequestFormProps = {}) => {
  const { t: translate } = useTranslation();
  const { closeSidePanel, showNotification, showApiError } = useUI();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Get employeeId from auth state
  const employeeId = useAppSelector((state: any) => state.auth.user?.idEmpleado);

  // Calculate default dates
  const getDefaultDates = useCallback(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const thirtyDaysLater = new Date(tomorrow);
    thirtyDaysLater.setDate(tomorrow.getDate() + 30);
    
    return {
      dateFrom: formatDateToYYYYMMDD(tomorrow),
      dateTo: formatDateToYYYYMMDD(thirtyDaysLater),
    };
  }, []);

  const {
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm<ShiftRequestFormData>({
    resolver: zodResolver(shiftRequestSchema),
    defaultValues: {
      ...getDefaultDates(),
      requesterComment: '',
    },
  });

  const dateFrom = watch('dateFrom');
  const dateTo = watch('dateTo');
  const requesterComment = watch('requesterComment');

  // Calculate minDate for dateTo (same as dateFrom)
  const getMinDateTo = useCallback(() => {
    if (dateFrom) {
      return dateFrom;
    }
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return formatDateToYYYYMMDD(tomorrow);
  }, [dateFrom]);

  // Calculate maxDate for dateTo (dateFrom + 30 days)
  const getMaxDateTo = useCallback(() => {
    const fromDate = dateFrom ? new Date(dateFrom + 'T00:00:00') : new Date();
    const maxDate = new Date(fromDate);
    maxDate.setDate(fromDate.getDate() + 30);
    return formatDateToYYYYMMDD(maxDate);
  }, [dateFrom]);

  const onSubmit = useCallback(async (data: ShiftRequestFormData) => {
    if (!employeeId) {
      showNotification('error', 'No se pudo obtener el ID del empleado', 4000);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await shiftService.submitShiftChangeRequest(employeeId, data);

      if (!response.success) {
        // Handle validation errors by mapping them to form fields
        if (response.error?.code === 'VALIDATION_ERROR' && response.error.validationErrors) {
          injectValidationErrors(response.error.validationErrors, { setError });
          showNotification('error', 'Corrige los errores de validación', 4000);
          setIsSubmitting(false);
          return;
        }

        // Handle other errors
        showApiError(response, translate('attendance:shiftChange.messages.createError'));
        setIsSubmitting(false);
        return;
      }

      showNotification('success', translate('attendance:shiftChange.messages.createSuccess'), 4000);
      closeSidePanel();
      
      if (onSuccess) {
        onSuccess();
      }
    } catch {
      showNotification('error', translate('attendance:shiftChange.messages.createError'), 4000);
      setIsSubmitting(false);
    }
  }, [employeeId, showApiError, showNotification, translate, closeSidePanel, onSuccess, setError]);

  const handleFormSubmit = useCallback((event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    void handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  return {
    // Form state
    dateFrom,
    dateTo,
    requesterComment,
    errors,
    isSubmitting,

    // Form actions
    setValue,
    handleFormSubmit,

    // Computed values
    minDateFrom: (() => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return formatDateToYYYYMMDD(tomorrow);
    })(),
    minDateTo: getMinDateTo(),
    maxDateTo: getMaxDateTo(),

    // Translations
    translate,
  };
};
